export function readReplayRange(url: string): { start: number; end: number | null } {
  const value = new URLSearchParams(url.split('#')[1] ?? '').get('t');
  if (!value || !/^\d+(?:,\d+)?$/.test(value)) return { start: 0, end: null };
  const [start, end] = value.split(',').map(Number);
  if (!Number.isSafeInteger(start) || (end != null && (!Number.isSafeInteger(end) || end <= start))) {
    return { start: 0, end: null };
  }
  return { start, end: end ?? null };
}

/** Owns start/resume/seek/end handling; progress stays in original-video seconds. */
export function bindReplayRange(video: HTMLVideoElement, url: string, resume: number, onEnded: () => void) {
  const range = readReplayRange(url);
  let initialized = false;
  let completed = false;
  const bounds = () => {
    const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : Infinity;
    // A replaced/corrupt duration must not trap the player in a seek loop.
    return { start: range.start < duration ? range.start : 0, end: Math.min(range.end ?? Infinity, duration) };
  };
  const complete = () => {
    if (completed) return;
    completed = true;
    video.pause();
    onEnded();
  };
  const enforce = () => {
    if (!initialized || completed) return;
    const { start, end } = bounds();
    if (video.currentTime >= end) {
      if (video.currentTime > end) video.currentTime = end;
      complete();
    } else if (video.currentTime < start) {
      video.currentTime = start;
    }
  };
  const initialize = () => {
    if (initialized) return;
    initialized = true;
    const { start, end } = bounds();
    const position = Math.max(start, Number.isFinite(resume) ? resume : 0);
    video.currentTime = Math.min(position, end);
    enforce();
  };
  video.addEventListener('loadedmetadata', initialize);
  for (const event of ['timeupdate', 'seeking', 'play', 'pause']) video.addEventListener(event, enforce);
  video.addEventListener('ended', complete);
  if (video.readyState >= 1) initialize();
  return () => {
    video.removeEventListener('loadedmetadata', initialize);
    for (const event of ['timeupdate', 'seeking', 'play', 'pause']) video.removeEventListener(event, enforce);
    video.removeEventListener('ended', complete);
  };
}
